import { useRef } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { SEO } from "@/components/site/SEO";
import { KineticText } from "@/components/site/KineticText";
import { RevealSection } from "@/components/site/RevealSection";
import { CTAButton } from "@/components/site/CTAButton";
import { FAQ } from "@/components/site/FAQ";
import { IsotypeLogo } from "@/components/site/IsotypeLogo";
import { StatCube } from "@/components/site/StatCube";
import { WhatWeDoBlocks } from "@/components/site/WhatWeDoBlocks";
import { SignalPulseLine } from "@/components/site/SignalPulseLine";
import { StaggerLines } from "@/components/site/StaggerLines";
import { AmbientGlow } from "@/components/site/AmbientGlow";
import { AuroraBackground } from "@/components/site/AuroraBackground";
import { AuroraGlow } from "@/components/site/AuroraGlow";
import { PostuladosMarquee } from "@/components/site/PostuladosMarquee";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAutoplayVideo } from "@/hooks/useAutoplayVideo";
import { motion, useScroll, useTransform } from "framer-motion";

// Character-by-character cascade — split into words first (so a line break
// never falls mid-word), each word's letters fade/rise in on their own
// stagger step. Used sparingly (one statement per page) since it spins up
// one viewport observer per letter. animate=false (mobile/tablet) renders
// the same text with no per-letter motion at all — plain text, instant.
function KineticChars({ text, className = "", charDelay = 0.014, animate = true }) {
  if (!animate) {
    return <span className={className}>{text}</span>;
  }

  const words = text.split(" ");
  let charIndex = 0;
  return (
    <span className={className}>
      {words.map((word, wi) => (
        <span key={wi} className="inline-block whitespace-nowrap mr-[0.28em] last:mr-0">
          {word.split("").map((ch, ci) => {
            const delay = charIndex * charDelay;
            charIndex += 1;
            return (
              <motion.span
                key={ci}
                className="inline-block"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay }}
              >
                {ch}
              </motion.span>
            );
          })}
        </span>
      ))}
    </span>
  );
}

// A small animated rule that draws itself in under an eyebrow label —
// a crisp, editorial "afilada" detail that repeats the same way everywhere
// it appears instead of introducing a new motion pattern per section.
// animate=false (mobile/tablet, outside hero/market) renders it already
// drawn in, no scaleX animation.
function EyebrowRule({ className = "", animate = true }) {
  if (!animate) {
    return <span className={`block h-px w-10 bg-accent mt-3 ${className}`} aria-hidden="true" />;
  }
  return (
    <motion.span
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`block h-px w-10 origin-left bg-accent mt-3 ${className}`}
      aria-hidden="true"
    />
  );
}

// Scroll-linked "two extremes and a center" layout for Section 08 — the
// US / Malinois / LATAM blocks drift inward as the reader scrolls through
// the block, echoing the convergence treatment used in Section 06.
// On mobile/tablet the scroll-linked drift/scale is skipped — the three
// blocks just render at their final position, no useTransform tracking.
function WhyConverge({ home }) {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 85%", "start 25%"] });
  const leftX = useTransform(scrollYProgress, [0, 1], [-40, 0]);
  const rightX = useTransform(scrollYProgress, [0, 1], [40, 0]);
  const sideOpacity = useTransform(scrollYProgress, [0, 0.7], [0, 1]);
  const centerScale = useTransform(scrollYProgress, [0.35, 1], [0.88, 1]);
  const centerOpacity = useTransform(scrollYProgress, [0.35, 1], [0, 1]);

  const leftStyle = isMobile ? {} : { x: leftX, opacity: sideOpacity };
  const rightStyle = isMobile ? {} : { x: rightX, opacity: sideOpacity };
  const centerStyle = isMobile ? {} : { scale: centerScale, opacity: centerOpacity };

  return (
    <div
      ref={ref}
      className="mt-16 flex flex-col md:grid md:grid-cols-3 md:items-start gap-14 md:gap-10"
    >
      <motion.div
        style={leftStyle}
        className="order-1 md:order-none max-w-xs mx-auto md:mx-0"
        data-testid="home-why-left"
      >
        <span className="font-mono text-xs tracking-[0.3em] uppercase text-accent">{home.why.left.label}</span>
        <p className="mt-4 font-body text-base md:text-lg text-paper/75 leading-relaxed [text-wrap:pretty]">
          {home.why.left.text}
        </p>
      </motion.div>

      <motion.div
        style={centerStyle}
        className="order-3 md:order-none flex flex-col items-center justify-center text-center"
        data-testid="home-why-center"
      >
        <IsotypeLogo className="h-14 md:h-16" invert />
        {home.why.center.map((line, i) => (
          <p key={i} className="mt-4 font-heading italic text-xl md:text-2xl text-paper leading-snug [text-wrap:balance]">
            {line}
          </p>
        ))}
      </motion.div>

      <motion.div
        style={rightStyle}
        className="order-2 md:order-none max-w-xs mx-auto md:mx-0 md:ml-auto md:text-right"
        data-testid="home-why-right"
      >
        <span className="font-mono text-xs tracking-[0.3em] uppercase text-accent">{home.why.right.label}</span>
        <p className="mt-4 font-body text-base md:text-lg text-paper/75 leading-relaxed [text-wrap:pretty]">
          {home.why.right.text}
        </p>
      </motion.div>
    </div>
  );
}

// v21 homepage — narrative sales-argument architecture. Order matters:
// C.W. → problem → context → consequences → thesis → opportunity →
// Malinois → differentiation → services → process → expectations →
// fit filter → FAQ → CTA. See v21 doc for the full brief.

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Home() {
  const { t, lang, path } = useLang();
  const home = t.home;

  // Text-reveal motion is switched off on mobile/tablet everywhere on this
  // page EXCEPT the hero, the market/economy stats block, and the "Lo que
  // hacemos" cards (WhatWeDoBlocks handles its own tone, untouched here) —
  // those three keep their animations on every device. Everywhere else,
  // `reveal(props)` returns the motion props unchanged on desktop and `{}`
  // on mobile, so the same `motion.p`/`motion.span` just renders at its
  // final visual state with no IntersectionObserver or animation attached.
  const isMobile = useIsMobile();
  const reveal = (props) => (isMobile ? {} : props);

  // The cinematic-break video sits below the fold — see useAutoplayVideo
  // for why a plain IntersectionObserver-driven .play() isn't enough on
  // iOS Safari (it needs an actual user-gesture unlock first).
  const heroVideoRef = useAutoplayVideo();

  // The closing line of "one more thing" doubles as the section's quiet
  // hero moment — its final clause (after the last comma) is pulled out
  // in the accent color so the statement lands with more weight.
  const closingStatement = home.oneMoreThing.paragraphs[home.oneMoreThing.paragraphs.length - 1];
  const closingSplitIndex = closingStatement.lastIndexOf(",");
  const closingLead = closingSplitIndex >= 0 ? closingStatement.slice(0, closingSplitIndex + 1) : "";
  const closingHighlight = closingSplitIndex >= 0 ? closingStatement.slice(closingSplitIndex + 1) : closingStatement;

  return (
    <div className="bg-paper text-ink">
      <SEO title={home.meta.title} description={home.meta.description} lang={lang} />

      {/* 01 — HERO / C.W.: text is the ONLY protagonist. Per v21 spec:
          no stock photo, no laptop mockup, no illustration, no collage,
          no big logo, no shine/glow effect — editorial typography and
          negative space carry the entire section. */}
      <section
        id="home-hero"
        className="relative min-h-[100vh] flex flex-col justify-center overflow-hidden px-6 md:px-12 pt-32 pb-24 bg-paper"
      >
        {!isMobile && <AuroraBackground />}
        <div className="relative max-w-5xl mx-auto w-full">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="block font-mono text-[11px] md:text-xs tracking-[0.25em] uppercase text-accent max-w-xl [text-wrap:pretty]"
            data-testid="home-hero-kicker"
          >
            {home.kicker}
          </motion.span>
          <EyebrowRule />

          <KineticText
            tag="h1"
            testId="home-hero-title"
            lines={[home.heroTitle]}
            className="mt-8 font-heading font-semibold italic text-4xl sm:text-5xl lg:text-6xl leading-[1.15] tracking-tight text-ink [text-wrap:balance] lg:max-w-[80%]"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-14 space-y-7 max-w-3xl"
            data-testid="home-hero-sub"
          >
            {home.heroSub.map((p, i) =>
              i === home.heroSubEmphasisIndex ? (
                <p
                  key={i}
                  className="font-heading italic text-2xl md:text-3xl text-ink leading-snug pt-2"
                >
                  {p}
                </p>
              ) : (
                <p key={i} className="font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty]">
                  {p}
                </p>
              )
            )}
          </motion.div>

          <div className="mt-16 flex flex-wrap items-center gap-6" data-testid="home-hero-ctas">
            <CTAButton to={path("contact")} label={home.heroCta} size="large" testId="home-hero-cta" />
            <button
              type="button"
              onClick={() => scrollToId("home-market")}
              className="font-mono text-xs uppercase tracking-[0.15em] text-secondary hover:text-ink transition-colors duration-300 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              data-testid="home-hero-scroll-cta"
            >
              {home.heroScrollCta}
              <span aria-hidden="true">↓</span>
            </button>
          </div>

          <div className="mt-20 max-w-md space-y-2" data-testid="home-hero-transition">
            {home.heroTransition.map((p, i) => (
              <p key={i} className="font-body text-sm text-secondary/80 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* 02 — EL MERCADO HISPANO: solid-color stat cards, one signal color
          per cube (orange/cobalt/lime), same palette as Services/Lo que
          hacemos so the numbers read as part of the same system. */}
      <RevealSection id="home-market" className="relative overflow-hidden bg-ink py-24 md:py-36" as="section">
        <AmbientGlow />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12" data-testid="home-market-block">
          <h2 className="font-heading italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-paper [text-wrap:balance] max-w-3xl">
            <KineticChars text={home.market.title} animate={!isMobile} />
          </h2>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6 items-stretch">
            {home.market.cubes.map((cube, i) => (
              <StatCube
                key={i}
                testId={`home-market-cube-${i}`}
                colorKey={["orange", "cobalt", "lime"][i % 3]}
                {...cube}
              />
            ))}
          </div>
          {home.market.source && (
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.15em] text-paper/40">
              {home.market.source}
            </p>
          )}

          <div className="mt-20 max-w-2xl">
            <StaggerLines
              lines={home.market.paragraphs}
              lineClassName="font-body text-lg md:text-xl text-paper/75 leading-[1.75] [text-wrap:pretty]"
            />
            <motion.p
              initial={{ opacity: 0.15, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 font-heading italic text-2xl md:text-3xl text-paper leading-snug"
            >
              {home.market.statement}
            </motion.p>
            <StaggerLines
              lines={home.market.closing}
              className="mt-8"
              lineClassName="font-body text-lg md:text-xl text-paper/75 leading-[1.75] [text-wrap:pretty]"
            />
          </div>
        </div>
      </RevealSection>

      {/* 03 — LA HISTORIA REAL DE C.W.: chronicle, narrow reading column. */}
      <RevealSection className="bg-paper py-24 md:py-36" as="section">
        <div className="max-w-2xl mx-auto px-6 md:px-12" data-testid="home-story-block">
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-accent">
            {home.storyReal.eyebrow}
          </span>
          <EyebrowRule animate={!isMobile} />
          <h2 className="mt-6 font-heading font-semibold italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-ink [text-wrap:balance]">
            {home.storyReal.title}
          </h2>

          <StaggerLines
            className="mt-12"
            lines={home.storyReal.paragraphs}
            lineClassName="font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty]"
            animate={!isMobile}
          />

          <motion.p
            {...reveal({
              initial: { opacity: 0.15, scale: 0.95 },
              whileInView: { opacity: 1, scale: 1 },
              viewport: { once: true, margin: "-15% 0px" },
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            })}
            className="my-12 text-center font-heading italic text-3xl md:text-4xl text-ink [text-wrap:balance]"
            data-testid="home-story-isolated-line"
          >
            {home.storyReal.isolatedLine}
          </motion.p>

          <StaggerLines
            lines={home.storyReal.paragraphs2}
            lineClassName="font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty]"
            animate={!isMobile}
          />
        </div>
      </RevealSection>

      {/* Big quote — its own visual moment. A giant faded mark frames it
          editorial-style, and the line lands with a brief tension-shake
          instead of a flat fade — it's a threat, it should land like one. */}
      <RevealSection className="relative overflow-hidden bg-ink py-28 md:py-44" as="section" variant="scale">
        {!isMobile && <AuroraGlow tone="brand" />}
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center" data-testid="home-story-quote">
          <motion.blockquote
            {...reveal({
              initial: { opacity: 0, scale: 0.92 },
              whileInView: { opacity: 1, scale: 1, x: [0, 5, -4, 2, 0] },
              viewport: { once: true, margin: "-10% 0px" },
              transition: {
                opacity: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                scale: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                x: { duration: 0.45, delay: 0.45, ease: "easeOut" },
              },
            })}
            className="font-heading italic text-4xl md:text-6xl lg:text-7xl text-paper leading-[1.1] [text-wrap:balance]"
          >
            {home.storyReal.quote}
          </motion.blockquote>
        </div>
      </RevealSection>

      <RevealSection className="bg-paper py-24 md:py-36" as="section">
        <div className="max-w-2xl mx-auto px-6 md:px-12" data-testid="home-story-block-2">
          <StaggerLines
            lines={home.storyReal.paragraphs3}
            lineClassName="font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty]"
            animate={!isMobile}
          />
          <div className="mt-10 border-l-2 border-accent pl-6 space-y-2">
            {home.storyReal.closingStatement.map((line, i) => (
              <motion.p
                key={i}
                {...reveal({
                  initial: { opacity: 0, y: 14 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, margin: "-10% 0px" },
                  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.08 },
                })}
                className="font-body text-lg md:text-xl text-ink leading-[1.75] [text-wrap:pretty]"
              >
                {line}
              </motion.p>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* 04 — LA REVELACIÓN: the story becomes a thesis. Louder aurora
          background + a larger, harder-landing title mark this as the
          page's turning point, not just another beat. */}
      <RevealSection className="relative overflow-hidden bg-ink py-28 md:py-44" as="section">
        {!isMobile && <AuroraGlow tone="brand" />}
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12" data-testid="home-revelation-block">
          <motion.span
            {...reveal({
              initial: { scaleX: 0 },
              whileInView: { scaleX: 1 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            })}
            className="block h-px w-16 bg-accent origin-left mb-8"
            aria-hidden="true"
          />
          <h2 className="font-heading font-semibold italic tracking-tight leading-[1.1] text-4xl md:text-6xl lg:text-7xl text-paper [text-wrap:balance]">
            {home.revelation.title}
          </h2>
          <StaggerLines
            className="mt-12 max-w-2xl"
            lines={home.revelation.paragraphs}
            lineClassName="font-body text-xl md:text-2xl text-paper/80 leading-[1.7] [text-wrap:pretty]"
            animate={!isMobile}
          />
        </div>
      </RevealSection>

      <RevealSection className="bg-paper py-24 md:py-36" as="section">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-testid="home-revelation-question">
          <motion.p
            {...reveal({
              initial: { opacity: 0.15, scale: 0.97 },
              whileInView: { opacity: 1, scale: 1 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
            })}
            className="font-heading italic text-2xl md:text-4xl text-ink leading-snug [text-wrap:balance] max-w-3xl"
          >
            {home.revelation.bigQuestion}
          </motion.p>
          <StaggerLines
            className="mt-8 max-w-2xl"
            lines={home.revelation.afterQuestion}
            lineClassName="font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty]"
            animate={!isMobile}
          />
        </div>
      </RevealSection>

      <RevealSection
        className={`relative overflow-hidden bg-ink ${isMobile ? "py-24" : "pb-28 md:pb-32"}`}
        as="section"
      >
        {!isMobile && <AuroraGlow tone="brand" className="opacity-60" />}
        <div
          className="relative z-10 max-w-[88rem] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center"
          data-testid="home-revelation-statement"
        >
          <div className="md:col-span-4 order-2 md:order-1 flex flex-col justify-center items-center md:items-start text-center md:text-left h-full w-full">
            <motion.p
              {...reveal({
                initial: { opacity: 0.2, y: 10 },
                whileInView: { opacity: 1, y: 0 },
                viewport: { once: true, margin: "-15% 0px" },
                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
              })}
              className="font-heading italic text-3xl md:text-5xl lg:text-6xl text-paper leading-[1.15] [text-wrap:balance]"
            >
              {home.revelation.statementPart1}
            </motion.p>
            <motion.p
              {...reveal({
                initial: { opacity: 0, scale: 0.85 },
                whileInView: { opacity: 1, scale: 1 },
                viewport: { once: true, margin: "-15% 0px" },
                transition: { duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 0.35 },
              })}
              className={`mt-4 font-heading font-semibold italic text-4xl md:text-6xl lg:text-7xl leading-[1.1] [text-wrap:balance] ${
                isMobile ? "text-paper" : "text-accent"
              }`}
            >
              {home.revelation.statementPart2}
            </motion.p>
          </div>

          {!isMobile && (
            <motion.div
              {...reveal({
                initial: { opacity: 0, y: 18 },
                whileInView: { opacity: 1, y: 0 },
                viewport: { once: true, margin: "-10% 0px" },
                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
              })}
              className="md:col-span-8 order-1 md:order-2 relative aspect-[3/2] overflow-hidden border border-paper/10 bg-ink"
            >
              <img
                src="/img/malinois-stonks-bw.jpg"
                alt=""
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-contain"
              />
            </motion.div>
          )}
        </div>
      </RevealSection>

      {/* 05 — ¿POR QUÉ NO PUEDES FACTURAR?: sticky title, diagnosis on the right. */}
      <RevealSection className="bg-paper py-24 md:py-36" as="section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12" data-testid="home-invoice-block">
          <div className="md:col-span-4 md:sticky md:top-32 md:self-start">
            <h2 className="font-heading font-semibold italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-ink [text-wrap:balance]">
              {home.whyCantInvoice.title}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <StaggerLines
              lines={home.whyCantInvoice.paragraphs}
              lineClassName="font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty]"
              animate={!isMobile}
            />
            <StaggerLines
              className="mt-6"
              lines={home.whyCantInvoice.closing}
              lineClassName="font-body text-lg md:text-xl text-ink leading-[1.75] [text-wrap:pretty] font-medium"
              animate={!isMobile}
            />
            <div className="pt-4">
              <CTAButton to={path("contact")} label={home.whyCantInvoice.cta} testId="home-invoice-cta" />
            </div>
          </div>
        </div>
      </RevealSection>

      {/* 06 — LA VENTAJA QUE YA TIENES: energy shift, convergence graphic. */}
      <RevealSection className="relative overflow-hidden bg-ink py-28 md:py-44" as="section">
        <AuroraGlow tone="brand" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12" data-testid="home-advantage-block">
          <motion.span
            {...reveal({
              initial: { scaleX: 0 },
              whileInView: { scaleX: 1 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            })}
            className="block h-px w-16 bg-accent origin-left mb-8"
            aria-hidden="true"
          />
          <h2 className="font-heading font-semibold italic tracking-tight leading-[1.1] text-4xl md:text-6xl text-paper [text-wrap:balance]">
            {home.advantage.title}
          </h2>
          <p className="mt-10 font-body text-lg md:text-xl text-paper/75 leading-[1.75] [text-wrap:pretty]">
            {home.advantage.intro}
          </p>
          <motion.p
            {...reveal({
              initial: { opacity: 0, scale: 0.9 },
              whileInView: { opacity: 1, scale: 1 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.55, ease: [0.34, 1.56, 0.64, 1] },
            })}
            className="mt-4 font-heading font-semibold italic text-3xl md:text-4xl text-accent leading-snug [text-wrap:balance] origin-left"
          >
            {home.advantage.emphasisLine}
          </motion.p>
          <StaggerLines
            className="mt-8"
            lines={home.advantage.paragraphs}
            lineClassName="font-body text-lg md:text-xl text-paper/75 leading-[1.75] [text-wrap:pretty]"
            animate={!isMobile}
          />
          <div className="mt-14">
            {home.advantage.statement.map((line, i) => (
              <motion.p
                key={i}
                {...reveal({
                  initial: i === 0 ? { opacity: 0, y: 10 } : { opacity: 0, scale: 0.85 },
                  whileInView: i === 0 ? { opacity: 1, y: 0 } : { opacity: 1, scale: 1 },
                  viewport: { once: true, margin: "-10% 0px" },
                  transition:
                    i === 0
                      ? { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
                      : { duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 0.25 },
                })}
                className={`font-heading italic leading-[1.1] [text-wrap:balance] origin-left ${
                  i === 0 ? "text-3xl md:text-4xl text-paper" : "mt-2 text-4xl md:text-6xl font-semibold text-accent"
                }`}
              >
                {line}
              </motion.p>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* 06.7 — PARA ESO EXISTE MALINOIS WRITERS: brand screen. */}
      <RevealSection className="bg-paper py-28 md:py-40" as="section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center" data-testid="home-forthat-block">
          <h2 className="font-heading font-semibold italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-ink [text-wrap:balance]">
            {home.forThatExists.title}
          </h2>
          <IsotypeLogo className="h-12 md:h-14 mx-auto mt-8" />
          <span className="mt-4 block font-mono text-[11px] tracking-[0.3em] uppercase text-accent">
            MALINOIS WRITERS
          </span>
          <EyebrowRule className="mx-auto" animate={!isMobile} />
          <StaggerLines
            className="mt-8"
            lines={home.forThatExists.paragraphs}
            lineClassName="font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty]"
            animate={!isMobile}
          />
        </div>
      </RevealSection>

      {/* 06.8 — REFUERZO: its own full-bleed band instead of a boxed card —
          the previous max-w-6xl rectangle floating inside the paper section
          never quite matched the page's width on desktop. As a real
          section it sits flush with every other dark band on the page, and
          the aurora glow finally has the full width to move in. */}
      {home.forThatExists.reinforcement && (
        <RevealSection className="relative overflow-hidden bg-ink py-24 md:py-36" as="section">
          {!isMobile && <AuroraGlow tone="brand" />}
          <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12" data-testid="home-forthat-reinforcement">
            <motion.span
              {...reveal({
                initial: { scaleX: 0 },
                whileInView: { scaleX: 1 },
                viewport: { once: true, margin: "-10% 0px" },
                transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
              })}
              className="block h-px w-12 bg-accent origin-left mb-6"
              aria-hidden="true"
            />
            {/* Line 1 sets up the negation, muted; line 2 is the payoff —
                bigger, brighter, and lands with a slight overshoot so the
                contrast between "not this" and "this instead" is felt,
                not just read. */}
            <motion.p
              {...reveal({
                initial: { opacity: 0, y: 14 },
                whileInView: { opacity: 1, y: 0 },
                viewport: { once: true, margin: "-10% 0px" },
                transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
              })}
              className="font-body font-semibold text-2xl md:text-4xl text-paper/55 leading-[1.15] [text-wrap:balance]"
            >
              {home.forThatExists.reinforcement.lines[0]}
            </motion.p>
            <motion.p
              {...reveal({
                initial: { opacity: 0, scale: 0.88 },
                whileInView: { opacity: 1, scale: 1 },
                viewport: { once: true, margin: "-10% 0px" },
                transition: { duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 0.2 },
              })}
              className="mt-1 font-body font-bold text-4xl md:text-7xl text-paper leading-[1.05] [text-wrap:balance] origin-left"
            >
              {home.forThatExists.reinforcement.lines[1]}
            </motion.p>
            <motion.p
              {...reveal({
                initial: { opacity: 0, y: 10 },
                whileInView: { opacity: 1, y: 0 },
                viewport: { once: true, margin: "-10% 0px" },
                transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.45 },
              })}
              className="mt-7 font-body text-base md:text-lg text-paper/70 leading-relaxed [text-wrap:pretty] max-w-xl"
            >
              {home.forThatExists.reinforcement.subtext}
            </motion.p>
          </div>
        </RevealSection>
      )}

      {/* Postulados ribbon — moved here from the /servicios closing block.
          Runs right above "Lo que hacemos" so the three chapters it names
          (Conversión, Video, Voz de Marca) act as a marquee intro to the
          section that explains each of them. */}
      <PostuladosMarquee phrases={home.whatWeDo.services.map((s) => s.title)} tone="paper" />

      {/* 07 — LO QUE HACEMOS: three capabilities, not a supermarket list.
          Same dark treatment as the "refuerzo" section right above it —
          bg-ink + AuroraGlow tone="brand" — so the two dark bands read as
          one continuous mood. WhatWeDoBlocks back to its default tone
          ("dark"), so inactive cards use paper text again. */}
      <RevealSection className="relative overflow-hidden bg-ink py-24 md:py-36" as="section">
        <AuroraGlow tone="brand" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12" data-testid="home-whatwedo-block">
          <h2 className="font-body font-bold tracking-tight leading-[1.15] text-3xl md:text-5xl text-paper [text-wrap:balance]">
            {home.whatWeDo.title}
          </h2>

          <div className="mt-12 max-w-none">
            <WhatWeDoBlocks services={home.whatWeDo.services} />
          </div>

          <div className="mt-12">
            <CTAButton to={path("services")} label={home.whatWeDo.cta} testId="home-services-cta" />
          </div>
        </div>
      </RevealSection>

      {/* 06.5 — CINEMATIC BREAK: full-bleed brand footage, a quiet visual
          pause before the brand statement. No copy, no contained box — the
          video fills the entire block edge-to-edge on every breakpoint. */}
      <RevealSection className="relative overflow-hidden bg-ink" as="section">
        <motion.div
          {...reveal({
            initial: { opacity: 0 },
            whileInView: { opacity: 1 },
            viewport: { once: true, margin: "-10% 0px" },
            transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
          })}
          className="relative w-full h-[60vh] md:h-[75vh] lg:h-[85vh] max-h-[820px]"
          data-testid="home-typewriter-block"
        >
          <video
            ref={heroVideoRef}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.08) brightness(0.92)" }}
            src="/video/malinois-hero-block.mp4"
            poster="/video/malinois-hero-block-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(20,17,15,0.35) 0%, transparent 18%, transparent 82%, rgba(20,17,15,0.35) 100%)",
            }}
            aria-hidden="true"
          />
        </motion.div>
      </RevealSection>

      {/* 09 — ¿POR QUÉ MALINOIS WRITERS?: two extremes and a center that
          converge together as the reader scrolls through the block. */}
      <RevealSection className="relative overflow-hidden bg-ink py-24 md:py-36" as="section">
        <AmbientGlow />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12" data-testid="home-why-block">
          <h2 className="text-center font-heading font-semibold italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-paper [text-wrap:balance]">
            {home.why.title}
          </h2>
          <WhyConverge home={home} />
        </div>
      </RevealSection>

      {/* 10 — ¿CÓMO TRABAJAMOS?: just the thinking, no step grid — a
          signal-pulse line stands in as the section's dynamic flourish. */}
      <RevealSection className="relative overflow-hidden bg-paper py-28 md:py-40" as="section">
        <AuroraGlow tone="brand-light" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12" data-testid="home-howwework-intro">
          <motion.span
            {...reveal({
              initial: { scaleX: 0 },
              whileInView: { scaleX: 1 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            })}
            className="block h-px w-16 bg-accent origin-left mb-8"
            aria-hidden="true"
          />
          <h2 className="font-body font-bold tracking-tight leading-[1.1] text-4xl md:text-6xl text-ink [text-wrap:balance]">
            {home.howWeWork.title}
          </h2>
          <motion.p
            {...reveal({
              initial: { opacity: 0, y: 14 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
            })}
            className="mt-8 font-body text-lg md:text-xl text-secondary leading-[1.75] [text-wrap:pretty] max-w-xl"
          >
            {home.howWeWork.intro[0]}
          </motion.p>
          {home.howWeWork.intro[1] && (
            <motion.p
              {...reveal({
                initial: { opacity: 0, scale: 0.92 },
                whileInView: { opacity: 1, scale: 1 },
                viewport: { once: true, margin: "-10% 0px" },
                transition: { duration: 0.55, ease: [0.34, 1.56, 0.64, 1], delay: 0.2 },
              })}
              className="mt-5 font-heading italic text-2xl md:text-4xl text-accent leading-snug [text-wrap:balance] max-w-xl origin-left"
            >
              {home.howWeWork.intro[1]}
            </motion.p>
          )}
          <motion.div
            {...reveal({
              initial: { opacity: 0, scaleX: 0 },
              whileInView: { opacity: 1, scaleX: 1 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.4 },
            })}
            className="mt-12 max-w-xs origin-left"
          >
            {!isMobile && <SignalPulseLine colorClass="bg-accent" trackClass="bg-ink/10" />}
          </motion.div>
        </div>
      </RevealSection>

      {/* 11 — UNA COSA MÁS: honest note, intimate tone — black background,
          FAQ right after stays white so the two sections read as a
          deliberate light/dark beat. */}
      <RevealSection className="relative overflow-hidden bg-ink py-24 md:py-36" as="section">
        <AmbientGlow tone="soft" />
        <div className="relative z-10 max-w-2xl mx-auto px-6 md:px-12" data-testid="home-fit-block">
          <h2 className="font-heading font-semibold italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-paper [text-wrap:balance]">
            {home.oneMoreThing.title}
          </h2>
          <StaggerLines
            className="mt-8"
            gap="space-y-4"
            lines={home.oneMoreThing.paragraphs.slice(0, -1)}
            lineClassName="font-body text-lg md:text-xl text-paper/75 leading-[1.75] [text-wrap:pretty]"
            animate={!isMobile}
          />
          <motion.div
            {...reveal({
              initial: { opacity: 0, y: 24 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, margin: "-10% 0px" },
              transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.25 },
            })}
            className="mt-14 pt-12 border-t border-paper/15"
          >
            <p className="font-heading italic font-medium tracking-tight leading-[1.3] text-2xl md:text-4xl text-paper [text-wrap:balance]">
              {closingLead}
              <span className="text-accentSoft not-italic font-semibold">{closingHighlight}</span>
            </p>
          </motion.div>
        </div>
      </RevealSection>

      {/* 12 — FAQ */}
      <FAQ title={t.faq.title} items={t.faq.items} />

      {/* 13 — CTA FINAL */}
      <RevealSection className="relative overflow-hidden bg-ink py-28 md:py-40" as="section" variant="scale">
        <AmbientGlow variant="double" tone="soft" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center" data-testid="home-final-cta-block">
          <h2 className="font-heading font-semibold italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-paper [text-wrap:balance]">
            {home.finalCall.title}
          </h2>
          <p className="mt-6 font-body text-lg md:text-xl text-paper/75 leading-[1.75] [text-wrap:pretty] max-w-xl mx-auto">
            {home.finalCall.subtitle}
          </p>
          <div className="mt-14 flex justify-center">
            <CTAButton
              to={path("contact")}
              label={home.finalCall.cta}
              size="large"
              testId="home-final-cta-button"
            />
          </div>
          <p
            className="mt-16 font-mono text-[11px] md:text-xs tracking-[0.3em] uppercase text-paper/50"
            data-testid="home-closing-tagline"
          >
            {home.finalCall.tagline}
          </p>
        </div>
      </RevealSection>
    </div>
  );
}
